import React, { useMemo } from 'react';
import { Check, ChevronDown, Download, History, Layers, X } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { Z_PA_SHELL_DROPDOWN } from '@/constants/zLayers';
import { ArtifactRenderer, KindBadge } from './registry';
import { ArtifactActions } from './ArtifactActions';
import { getArtifactExportOptions } from './render/artifactExport';
import { formatRelativeTime } from './chatFormat';
import type { ArtifactFormSubmission } from '@easyflow/shared';
import type { WorkspaceUiArtifact } from './types';

// Task #1445 — viewer-only contract. La selezione/lista artefatti vive nella
// drawer della SideNav; questo componente mostra il contenuto dell'artefatto
// attivo. Task #3817: header con selettore versioni + export raggruppato per
// kind e plumbing `onFormSubmit` (layout e dimensioni del pannello INVARIATI).
interface Props {
  artifact: WorkspaceUiArtifact;
  onClose?: () => void;
  onAction?: (artifact: WorkspaceUiArtifact, intent: string) => void;
  onFormSubmit?: (artifact: WorkspaceUiArtifact, submission: ArtifactFormSubmission) => void;
  /** Tutte le versioni (stessa conversazione+slug) per il selettore; opzionale. */
  versions?: WorkspaceUiArtifact[];
  onSelectVersion?: (artifactId: string) => void;
}

export const ArtifactPanel: React.FC<Props> = ({ artifact, onClose, onAction, onFormSubmit, versions, onSelectVersion }) => {
  const exportOptions = useMemo(() => getArtifactExportOptions(artifact), [artifact]);
  const sortedVersions = useMemo(
    () => (versions ?? []).slice().sort((a, b) => b.version - a.version),
    [versions],
  );
  const showVersionSelector = sortedVersions.length > 1 && Boolean(onSelectVersion);

  return (
    <div className="flex flex-col h-full bg-muted border-l border-border">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
          <Layers className="h-4 w-4 text-violet-600 shrink-0" />
          <KindBadge kind={artifact.kind} />
          <span className="truncate">{artifact.title}</span>
          {!showVersionSelector && (
            <span className="text-[10px] text-muted-foreground font-normal shrink-0">v{artifact.version}</span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {showVersionSelector && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground" title="Versioni artefatto">
                  <History className="h-3 w-3" />
                  v{artifact.version}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: Z_PA_SHELL_DROPDOWN }} className="w-56">
                <DropdownMenuLabel className="text-[11px] text-muted-foreground">Versioni</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {sortedVersions.map(v => (
                  <DropdownMenuItem
                    key={v.id}
                    onSelect={() => { if (v.id !== artifact.id) onSelectVersion?.(v.id); }}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span className="font-medium">v{v.version}</span>
                    <span className="flex-1 truncate text-muted-foreground">{formatRelativeTime(v.updatedAt)}</span>
                    {v.id === artifact.id && <Check className="h-3.5 w-3.5 text-violet-600 shrink-0" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {exportOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground" title="Esporta artefatto">
                  <Download className="h-3 w-3" />
                  Esporta
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: Z_PA_SHELL_DROPDOWN }} className="w-48">
                {exportOptions.map(opt => (
                  <DropdownMenuItem key={opt.id} onSelect={() => { void opt.run(); }} className="text-xs">
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Task #3846 — Scarica/Condividi per ogni tipo di artefatto */}
          <ArtifactActions artifact={artifact} />
          {onClose && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={onClose} title="Chiudi pannello">
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {artifact.summary && (
        <div className="px-3 py-1.5 text-xs text-muted-foreground bg-card border-b border-border">
          {artifact.summary}
        </div>
      )}
      <ScrollArea className="flex-1">
        <div className="p-3">
          <ArtifactRenderer
            artifact={artifact}
            onAction={(intent) => onAction?.(artifact, intent)}
            onFormSubmit={onFormSubmit ? (submission) => onFormSubmit(artifact, submission) : undefined}
          />
        </div>
      </ScrollArea>
    </div>
  );
};
